import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Users, CheckCircle, Clock, AlertTriangle, CreditCard, Package, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const STAT_CARDS = [
  { key: 'totalUsers', label: 'Total Users', icon: <Users size={22} />, color: '#3b82f6' },
  { key: 'activeUsers', label: 'Active Subscriptions', icon: <CheckCircle size={22} />, color: '#10b981' },
  { key: 'trialUsers', label: 'Trial Users', icon: <Clock size={22} />, color: '#f59e0b' },
  { key: 'expiredUsers', label: 'Expired / Lapsed', icon: <AlertTriangle size={22} />, color: '#ef4444' },
  { key: 'paidUsers', label: 'Paid Users', icon: <CreditCard size={22} />, color: '#8b5cf6' },
];

export default function AdminDashboard() {
  const { adminToken } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setStats(res.data.stats);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div>
        <h1 style={styles.pageTitle}>Dashboard Overview</h1>
        <p style={styles.pageSub}>Welcome back, Admin. Here's what's happening with your platform.</p>

        {error && <div style={styles.error}>{error}</div>}

        {loading ? (
          <div style={styles.loading}>Loading stats...</div>
        ) : stats && (
          <>
            {/* Stat Cards */}
            <div style={styles.statsGrid}>
              {STAT_CARDS.map(card => (
                <div key={card.key} style={{ ...styles.statCard, borderTop: `3px solid ${card.color}` }}>
                  <div style={styles.statHeader}>
                    <span style={styles.statIcon}>{card.icon}</span>
                    <span style={{ ...styles.statValue, color: card.color }}>{stats[card.key] ?? 0}</span>
                  </div>
                  <div style={styles.statLabel}>{card.label}</div>
                </div>
              ))}
            </div>

            {/* Plan Breakdown */}
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}><Package size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Plan Distribution</h2>
              <div style={styles.planGrid}>
                {(stats.planBreakdown || []).map(p => (
                  <div key={p._id} style={styles.planCard}>
                    <div style={styles.planName}>{p._id?.charAt(0).toUpperCase() + p._id?.slice(1)}</div>
                    <div style={styles.planCount}>{p.count}</div>
                    <div style={styles.planLabel}>users</div>
                  </div>
                ))}
              </div>
            </div>

            {/* User Growth */}
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}><TrendingUp size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} /> New Registrations (Last 6 Months)</h2>
              <div style={styles.growthTable}>
                <div style={styles.growthHeader}>
                  <span>Month</span>
                  <span>New Users</span>
                  <span>Bar</span>
                </div>
                {(stats.userGrowth || []).map((g, i) => {
                  const maxCount = Math.max(...(stats.userGrowth || []).map(x => x.count), 1);
                  const pct = Math.round((g.count / maxCount) * 100);
                  const monthName = new Date(g._id.year, g._id.month - 1).toLocaleString('default', { month: 'short', year: '2-digit' });
                  return (
                    <div key={i} style={styles.growthRow}>
                      <span style={styles.growthMonth}>{monthName}</span>
                      <span style={styles.growthCount}>{g.count}</span>
                      <div style={styles.barTrack}>
                        <div style={{ ...styles.barFill, width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
                {(!stats.userGrowth || stats.userGrowth.length === 0) && (
                  <div style={styles.empty}>No registration data yet.</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

const styles = {
  pageTitle: { fontSize: '24px', fontWeight: '700', color: '#fff', margin: '0 0 6px' },
  pageSub: { fontSize: '14px', color: 'rgba(255,255,255,0.4)', margin: '0 0 28px' },
  loading: { color: 'rgba(255,255,255,0.5)', padding: '40px', textAlign: 'center', fontSize: '15px' },
  error: { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', padding: '12px 16px', borderRadius: '10px', marginBottom: '20px' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px', marginBottom: '32px' },
  statCard: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '12px',
    padding: '20px',
    transition: 'transform 0.2s',
  },
  statHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  statIcon: { fontSize: '22px' },
  statValue: { fontSize: '30px', fontWeight: '800', lineHeight: 1 },
  statLabel: { fontSize: '13px', color: 'rgba(255,255,255,0.4)', fontWeight: '500' },
  section: { marginBottom: '32px' },
  sectionTitle: { fontSize: '16px', fontWeight: '600', color: '#fff', marginBottom: '16px' },
  planGrid: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  planCard: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '12px',
    padding: '20px 32px',
    textAlign: 'center',
    minWidth: '120px',
  },
  planName: { fontSize: '13px', color: '#f59e0b', fontWeight: '600', marginBottom: '8px', textTransform: 'capitalize' },
  planCount: { fontSize: '36px', fontWeight: '800', color: '#fff' },
  planLabel: { fontSize: '12px', color: 'rgba(255,255,255,0.4)' },
  growthTable: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  growthHeader: {
    display: 'grid',
    gridTemplateColumns: '100px 80px 1fr',
    padding: '12px 20px',
    background: 'rgba(255,255,255,0.05)',
    fontSize: '12px',
    color: 'rgba(255,255,255,0.4)',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  growthRow: {
    display: 'grid',
    gridTemplateColumns: '100px 80px 1fr',
    padding: '12px 20px',
    borderTop: '1px solid rgba(255,255,255,0.05)',
    alignItems: 'center',
  },
  growthMonth: { fontSize: '14px', color: '#fff', fontWeight: '500' },
  growthCount: { fontSize: '14px', color: '#f59e0b', fontWeight: '700' },
  barTrack: { height: '8px', background: 'rgba(255,255,255,0.06)', borderRadius: '4px', overflow: 'hidden' },
  barFill: { height: '100%', background: 'linear-gradient(90deg, #f59e0b, #ef4444)', borderRadius: '4px', transition: 'width 0.6s ease' },
  empty: { padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '14px' },
};
